/**
 * TDK（title / keywords / description）缓存与应用
 *
 * 设计目的：首屏与路由切换时先同步读取本地缓存写入 <head>，接口只在后台刷新缓存。
 * 这样标题在第一帧就是最终值，消除"静态标题 → 接口返回后跳变"的闪烁，
 * 同时路由切换不再被 TDK 请求阻塞。
 */
import { getTDK } from '@/api/home'

const CACHE_KEY = 'tdk_cache_v1'

// 后端 location → 前端路由 name（与 src/router/index.js 路由表对应）
const LOCATION_TO_ROUTE = {
  home: 'home',
  keymos: 'keymos',
  product: 'product',
  solution: 'solution',
  about_us: 'about_us',
  contact_us: 'contactus',
  news: 'news',
  service_and_support: 'service_and_support',
}

function readCache() {
  try {
    const data = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')
    return data && Array.isArray(data.list) && data.list.length ? data.list : null
  } catch (e) {
    return null
  }
}

function writeCache(list) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), list }))
  } catch (e) {
    // 隐私模式 / 超出配额：静默忽略，退化为无缓存
  }
}

// 取当前语言的三个字段，英文缺失时回退中文
function pickLang(item, lang) {
  const zh = lang !== 'en-US'
  return {
    title: (zh ? item.page_title : item.page_title_en) || item.page_title,
    keywords: (zh ? item.page_keywords : item.page_keywords_en) || item.page_keywords,
    description: (zh ? item.page_description : item.page_description_en) || item.page_description,
  }
}

/**
 * 同步应用 TDK：只读缓存、不发请求。
 * @returns {boolean} 是否命中缓存（未命中时调用方通常需要等待一次接口）
 */
export function applyTdk(routeName, lang) {
  const list = readCache()
  if (!list) return false

  const item = list.find(el => LOCATION_TO_ROUTE[el.location] === routeName)
  if (!item) return false

  const { title, keywords, description } = pickLang(item, lang)

  if (title) document.title = title

  const setMeta = (name, content) => {
    if (!content) return
    let el = document.head.querySelector(`meta[name="${name}"]`)
    if (!el) {
      el = document.createElement('meta')
      el.setAttribute('name', name)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }
  setMeta('keywords', keywords)
  setMeta('description', description)
  return true
}

let inflight = null

/**
 * 异步刷新 TDK：同一时刻只发一个请求，成功后写入缓存。
 * 只负责"取 + 存"，是否覆盖当前页面标题由调用方（路由守卫）决定。
 */
export function fetchTdk() {
  if (inflight) return inflight
  inflight = getTDK()
    .then(res => {
      if (res && res.code === 200001 && res.data && Array.isArray(res.data.list)) {
        writeCache(res.data.list)
      }
    })
    .catch(() => {
      // 请求失败不影响页面：缓存仍然可用
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

/** 清空缓存（调试用；也可在后台改完 TDK 后调用以立即生效） */
export function clearTdkCache() {
  try {
    localStorage.removeItem(CACHE_KEY)
  } catch (e) {
    // ignore
  }
}
